import React, { useState, useEffect } from 'react';
import { ForumAPI } from '../../utils/api';
import { getToken } from '../../utils/auth';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaUser, FaClock, FaBookOpen, FaBullhorn, FaUsers, FaShieldAlt, FaArrowLeft } from 'react-icons/fa';
import RoleBadge from '../../components/RoleBadge';
import ConfirmDialog from '../../components/Admin/ConfirmDialog';
import SpinnerLoading from '../../components/SpinnerLoading';

const ForumApprovalPage: React.FC = () => {
    const navigate = useNavigate();
    const [news, setNews] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const [viewingNews, setViewingNews] = useState<any | null>(null);

    // Confirm Dialog State
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: '',
        message: '',
        isDestructive: false,
        onConfirm: () => { }
    });

    useEffect(() => {
        loadPendingNews();
    }, []);

    const loadPendingNews = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;

            const response = await ForumAPI.list({
                status: 'pending',
                limit: 100
            }, token);

            // Process URLs
            const { getApiBaseUrl } = await import('../../utils/api');
            const API_URL = getApiBaseUrl();
            const baseUrl = API_URL.split('/api')[0];
            const processUrl = (url: string) =>
                url && url.startsWith('http') ? url : url ? `${baseUrl}${url}` : '';

            const processed = response.news.map((item: any) => ({
                ...item,
                image: item.image ? processUrl(item.image) : null,
                author: {
                    ...item.author,
                    avatar: item.author?.avatar ? processUrl(item.author.avatar) : null
                }
            }));

            setNews(processed);
        } catch (error) {
            console.error('Load pending news error:', error);
            toast.error('Không thể tải danh sách bài viết cần duyệt');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmState({
            isOpen: true,
            title: 'Duyệt bài viết',
            message: 'Bạn có chắc chắn muốn duyệt bài viết này không?',
            isDestructive: false,
            onConfirm: async () => {
                setConfirmState(prev => ({ ...prev, isOpen: false }));
                try {
                    setActionLoading(id);
                    const token = getToken();
                    if (!token) return;

                    await ForumAPI.approve(id, token);
                    toast.success('Đã duyệt bài viết');
                    setNews(prev => prev.filter(n => n.id !== id));
                    setViewingNews(null); // Close modal if open
                } catch (error) {
                    console.error('Approve error:', error);
                    toast.error('Có lỗi xảy ra khi duyệt bài');
                } finally {
                    setActionLoading(null);
                }
            }
        });
    };

    const handleReject = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmState({
            isOpen: true,
            title: 'Từ chối bài viết',
            message: 'Bài viết sẽ bị từ chối và ẩn khỏi hệ thống. Bạn có chắc chắn không?',
            isDestructive: true,
            onConfirm: async () => {
                setConfirmState(prev => ({ ...prev, isOpen: false }));
                try {
                    setActionLoading(id);
                    const token = getToken();
                    if (!token) return;

                    await ForumAPI.reject(id, token);
                    toast.success('Đã từ chối bài viết');
                    setNews(prev => prev.filter(n => n.id !== id));
                    setViewingNews(null); // Close modal if open
                } catch (error) {
                    console.error('Reject error:', error);
                    toast.error('Có lỗi xảy ra khi từ chối bài');
                } finally {
                    setActionLoading(null);
                }
            }
        });
    };

    const getCategoryLabel = (cat: string) => {
        switch (cat) {
            case 'bqt': return { label: 'Thông Báo BQT', color: 'bg-red-100 text-red-600', icon: FaBullhorn };
            case 'bcs': return { label: 'Góc Học Tập', color: 'bg-blue-100 text-blue-600', icon: FaBookOpen };
            case 'general': return { label: 'Thảo Luận', color: 'bg-green-100 text-green-600', icon: FaUsers };
            default: return { label: 'Khác', color: 'bg-gray-100 text-gray-600', icon: FaShieldAlt };
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div style={{ transform: 'scale(0.435)' }}>
                    <SpinnerLoading />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12 transition-colors duration-200">

            {/* Header Gradient */}
            <div className="relative overflow-hidden group bg-gradient-to-br from-orange-600 via-orange-700 to-red-900 dark:from-orange-900 dark:via-red-950 dark:to-red-950 shadow-2xl animate-slideDownIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12 relative z-10">


                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-3 tracking-tight">Duyệt bài viết</h1>
                            <p className="text-base font-mono sm:text-lg text-orange-100 dark:text-orange-200">Quản lý và phê duyệt các bài viết mới từ thành viên</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-white font-mono text-sm">
                            <span className="font-bold text-lg">{news.length}</span> bài chờ duyệt
                        </div>
                    </div>
                </div>
            </div>

            {/* Back Button - Positioned below Hero Section */}
            <div className="max-w-screen-2xl mx-auto px-6 pt-4">
                <button
                    onClick={() => navigate('/forum')}
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors font-medium group mb-6"
                >
                    <div className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm group-hover:shadow-md transition-all border border-gray-200 dark:border-gray-700">
                        <FaArrowLeft className="group-hover:-translate-x-0.5 transition-transform text-sm" />
                    </div>
                    <span>Quay lại</span>
                </button>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-20 animate-slideUpIn">
                {news.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center shadow-xl border border-gray-200 dark:border-gray-700">
                        <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaCheck className="text-green-500 text-2xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Tất cả bài viết đã được duyệt</h3>
                        <p className="text-gray-500 dark:text-gray-400">Hiện không có bài viết nào đang chờ phê duyệt.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {news.map(item => {
                            const CategoryInfo = getCategoryLabel(item.category);
                            const CatIcon = CategoryInfo.icon;

                            return (
                                <div
                                    key={item.id}
                                    onClick={() => setViewingNews(item)}
                                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all cursor-pointer group"
                                >
                                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                                        {/* info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${CategoryInfo.color}`}>
                                                    <CatIcon className="text-[10px]" />
                                                    {CategoryInfo.label}
                                                </span>
                                                <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                                                    {item.title}
                                                </h3>
                                            </div>

                                            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                                <div className="flex items-center gap-1">
                                                    {item.author.avatar ? (
                                                        <img src={item.author.avatar} alt={item.author.name} className="w-4 h-4 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold">
                                                            {item.author.name[0]}
                                                        </div>
                                                    )}
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                                        {item.author.name}
                                                    </span>
                                                    <RoleBadge role={item.author.role} />
                                                </div>
                                                <span>•</span>
                                                <div className="flex items-center gap-1">
                                                    <FaClock className="text-[10px]" />
                                                    {new Date(item.createdAt).toLocaleString('vi-VN')}
                                                </div>
                                            </div>

                                            {/* Preview content optional or removed for compactness, keeping tiny preview */}
                                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">
                                                {item.content}
                                            </p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-0 border-gray-100 dark:border-gray-700">
                                            <button
                                                onClick={(e) => handleApprove(item.id, e)}
                                                disabled={actionLoading === item.id}
                                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-bold text-xs transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                                            >
                                                {actionLoading === item.id ? '...' : <><FaCheck /> Duyệt</>}
                                            </button>
                                            <button
                                                onClick={(e) => handleReject(item.id, e)}
                                                disabled={actionLoading === item.id}
                                                className="px-3 py-1.5 bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md font-bold text-xs transition-colors disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                                            >
                                                <FaTimes /> Từ chối
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {viewingNews && (
                <div className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6 animate-fadeIn">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setViewingNews(null)}></div>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col relative z-10 animate-slideUpIn border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {/* Modal Header */}
                        <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-start gap-4 bg-gray-50 dark:bg-gray-800">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${getCategoryLabel(viewingNews.category).color}`}>
                                        {getCategoryLabel(viewingNews.category).label}
                                    </span>
                                    <span className="text-gray-400 text-xs">•</span>
                                    <span className="text-gray-500 dark:text-gray-400 text-xs flex items-center gap-1">
                                        <FaClock className="text-[10px]" />
                                        {new Date(viewingNews.createdAt).toLocaleString('vi-VN')}
                                    </span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                                    {viewingNews.title}
                                </h2>
                            </div>
                            <button
                                onClick={() => setViewingNews(null)}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar min-h-0">
                            {/* Author Badge */}
                            <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
                                {viewingNews.author.avatar ? (
                                    <img src={viewingNews.author.avatar} alt={viewingNews.author.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-800" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm ring-2 ring-white dark:ring-gray-800">
                                        {viewingNews.author.name[0]}
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-gray-900 dark:text-white">{viewingNews.author.name}</span>
                                        <RoleBadge role={viewingNews.author.role} />
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Tác giả bài viết</p>
                                </div>
                            </div>

                            {/* Cover Image */}
                            {viewingNews.image && (
                                <div className="mb-6 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                                    <img src={viewingNews.image} alt={viewingNews.title} className="w-full h-auto object-cover max-h-[400px]" />
                                </div>
                            )}

                            {/* Content */}
                            <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                                <p className="whitespace-pre-wrap leading-relaxed">{viewingNews.content}</p>
                            </div>
                        </div>

                        {/* Modal Footer (Actions) */}
                        <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-2xl flex justify-end gap-3 z-10 relative">
                            <button
                                onClick={(e) => handleReject(viewingNews.id, e)}
                                className="px-5 py-2.5 bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 border border-gray-200 dark:border-gray-600 hover:border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl font-bold text-sm transition-all focus:ring-4 focus:ring-red-100 dark:focus:ring-red-900/30 flex items-center gap-2"
                            >
                                <FaTimes /> Từ chối
                            </button>
                            <button
                                onClick={(e) => handleApprove(viewingNews.id, e)}
                                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 flex items-center gap-2"
                            >
                                <FaCheck /> Duyệt bài
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                message={confirmState.message}
                isDestructive={confirmState.isDestructive}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
};

export default ForumApprovalPage;
